import {
  UserPlus, Mail, ShieldCheck, Shield, Unlock, Trash2, KeyRound,
  CheckCircle, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Never';
  return new Date(timestamp).toLocaleString();
};

export default function AdminManagementTab({
  adminsList,
  inviteAdminOpen, setInviteAdminOpen,
  newAdmin, setNewAdmin,
  inviting, handleInviteAdmin,
  handleUnlockAdmin, handleRevokeAdmin, handleForceDisableMfa,
  mfaStatus, fetchMfaStatus,
  mfaBackupCodes, setMfaBackupCodes,
  mfaSetupData, setMfaSetupData,
  mfaVerifyCode, setMfaVerifyCode,
  mfaDisablePassword, setMfaDisablePassword,
  mfaLoading,
  handleMfaSetup, handleMfaVerify, handleMfaDisable,
}) {
  return (
    <div data-testid="admin-management-tab-content">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-display text-2xl text-navy">Admin Management</h2>
          <p className="text-text-mid text-sm mt-1">Manage admin accounts and permissions</p>
        </div>
        <Dialog open={inviteAdminOpen} onOpenChange={setInviteAdminOpen}>
          <DialogTrigger asChild>
            <Button className="bg-teal hover:bg-teal-deep rounded-full" data-testid="invite-admin-btn">
              <UserPlus size={18} className="mr-2" /> Invite Admin
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display text-navy">Invite New Admin</DialogTitle>
              <DialogDescription>
                Send an invitation email to grant admin access. They will receive a link to set up their account.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="invite-first-name">First Name</Label>
                  <Input
                    id="invite-first-name"
                    value={newAdmin.firstName}
                    onChange={(e) => setNewAdmin({ ...newAdmin, firstName: e.target.value })}
                    placeholder="Jane"
                    className="rounded-xl"
                    data-testid="invite-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite-last-name">Last Name</Label>
                  <Input
                    id="invite-last-name"
                    value={newAdmin.lastName}
                    onChange={(e) => setNewAdmin({ ...newAdmin, lastName: e.target.value })}
                    placeholder="Smith"
                    className="rounded-xl"
                    data-testid="invite-last-name"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email Address</Label>
                <Input
                  id="invite-email"
                  type="email"
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                  placeholder="admin@example.com"
                  className="rounded-xl"
                  data-testid="invite-email"
                />
              </div>
              <Button
                onClick={handleInviteAdmin}
                disabled={inviting || !newAdmin.email || !newAdmin.firstName || !newAdmin.lastName}
                className="w-full bg-teal hover:bg-teal-deep rounded-full"
                data-testid="send-invite-btn"
              >
                {inviting ? (
                  <><Loader2 className="mr-2 animate-spin" size={18} /> Sending...</>
                ) : (
                  <><Mail size={18} className="mr-2" /> Send Invitation</>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last Login</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {adminsList.map((adminItem) => (
              <TableRow key={adminItem.id}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {adminItem.first_name} {adminItem.last_name}
                    {adminItem.role === 'super_admin' && (
                      <ShieldCheck size={16} className="text-amber-500" />
                    )}
                  </div>
                </TableCell>
                <TableCell>{adminItem.email}</TableCell>
                <TableCell>
                  <Badge
                    className={adminItem.role === 'super_admin'
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-blue-100 text-blue-800'}
                  >
                    {adminItem.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                  </Badge>
                </TableCell>
                <TableCell>
                  {adminItem.is_active ? (
                    <Badge className="bg-green-100 text-green-800">Active</Badge>
                  ) : (
                    <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>
                  )}
                </TableCell>
                <TableCell className="text-sm text-text-mid">
                  {formatTimestamp(adminItem.last_login)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {adminItem.role !== 'super_admin' && adminItem.is_deletable && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleUnlockAdmin(adminItem.id, adminItem.email)}
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                          title="Unlock account"
                          data-testid={`unlock-admin-${adminItem.id}`}
                        >
                          <Unlock size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRevokeAdmin(adminItem.id, adminItem.email)}
                          className="text-red-600 hover:text-red-800 hover:bg-red-50"
                          title="Revoke access"
                          data-testid={`revoke-admin-${adminItem.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </>
                    )}
                    {adminItem.role === 'super_admin' && (
                      <span className="text-xs text-text-light">Protected</span>
                    )}
                    {adminItem.mfa_enabled && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleForceDisableMfa(adminItem.id, adminItem.email)}
                        className="text-orange-600 hover:text-orange-800 hover:bg-orange-50"
                        title="Force-disable MFA"
                        data-testid={`force-mfa-${adminItem.id}`}
                      >
                        <KeyRound size={16} />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* MFA Security Section */}
      <div className="bg-white rounded-2xl shadow-card p-6 mt-6" data-testid="mfa-section">
        <h3 className="font-semibold text-navy mb-1 flex items-center gap-2">
          <KeyRound size={18} className="text-teal" />
          Two-Factor Authentication (MFA)
        </h3>
        <p className="text-sm text-text-mid mb-4">Secure your admin account with an authenticator app.</p>

        {!mfaStatus ? (
          <Button onClick={fetchMfaStatus} variant="outline" size="sm" className="rounded-full">Check Status</Button>
        ) : mfaBackupCodes ? (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-yellow-800 mb-2">Save Your Backup Codes</p>
              <p className="text-xs text-yellow-700 mb-3">Store these in a secure place. Each code can only be used once. They will not be shown again.</p>
              <div className="grid grid-cols-2 gap-2">
                {mfaBackupCodes.map((code, i) => (
                  <code key={i} className="bg-white px-3 py-1.5 rounded-lg text-sm font-mono text-navy text-center border" data-testid={`backup-code-${i}`}>{code}</code>
                ))}
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(mfaBackupCodes.join('\n'));
                toast.success('Backup codes copied to clipboard');
              }}
              variant="outline"
              className="rounded-full"
            >
              Copy All Codes
            </Button>
            <Button
              size="sm"
              onClick={() => setMfaBackupCodes(null)}
              className="rounded-full bg-teal hover:bg-teal-deep text-white ml-2"
            >
              Done
            </Button>
          </div>
        ) : mfaSetupData ? (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-start gap-6">
              <div className="bg-white border rounded-xl p-4 flex-shrink-0">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(mfaSetupData.provisioning_uri)}`}
                  alt="MFA QR Code"
                  width={200}
                  height={200}
                  data-testid="mfa-qr-code"
                />
              </div>
              <div className="space-y-3 flex-1">
                <p className="text-sm text-navy font-medium">1. Scan this QR code with your authenticator app</p>
                <p className="text-xs text-text-mid">(Google Authenticator, Authy, 1Password, etc.)</p>
                <p className="text-sm text-navy font-medium mt-3">2. Enter the 6-digit code shown in the app</p>
                <div className="flex gap-2 max-w-xs">
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={mfaVerifyCode}
                    onChange={(e) => setMfaVerifyCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="rounded-xl text-center font-mono text-lg tracking-widest"
                    data-testid="mfa-setup-code-input"
                  />
                  <Button
                    onClick={handleMfaVerify}
                    disabled={mfaLoading || mfaVerifyCode.length < 6}
                    className="bg-teal hover:bg-teal-deep rounded-full px-6"
                    data-testid="mfa-setup-verify-btn"
                  >
                    {mfaLoading ? <Loader2 size={16} className="animate-spin" /> : 'Verify'}
                  </Button>
                </div>
                <div className="mt-2">
                  <p className="text-xs text-text-mid">Manual entry key:</p>
                  <code className="text-xs font-mono bg-cream/50 px-2 py-1 rounded select-all">{mfaSetupData.secret}</code>
                </div>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setMfaSetupData(null)} className="text-text-mid">Cancel</Button>
          </div>
        ) : mfaStatus.mfa_enabled ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
              <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
              <div>
                <p className="text-sm font-semibold text-green-800">MFA is active</p>
                <p className="text-xs text-green-700">
                  Enabled {mfaStatus.enabled_at ? new Date(mfaStatus.enabled_at).toLocaleDateString() : ''} — {mfaStatus.backup_codes_remaining} backup codes remaining
                </p>
              </div>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1 max-w-xs">
                <Label className="text-xs text-text-mid">Enter password to disable MFA</Label>
                <Input
                  type="password"
                  value={mfaDisablePassword}
                  onChange={(e) => setMfaDisablePassword(e.target.value)}
                  placeholder="Your password"
                  className="rounded-xl"
                  data-testid="mfa-disable-password"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleMfaDisable}
                disabled={mfaLoading || !mfaDisablePassword}
                className="text-red-600 border-red-300 hover:bg-red-50 rounded-full"
                data-testid="mfa-disable-btn"
              >
                Disable MFA
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3 bg-cream/50 border border-border-subtle rounded-xl p-4">
              <Shield className="text-text-mid flex-shrink-0" size={20} />
              <div>
                <p className="text-sm font-medium text-navy">MFA is not enabled</p>
                <p className="text-xs text-text-mid">Add an extra layer of security to your admin account</p>
              </div>
            </div>
            <Button
              onClick={handleMfaSetup}
              disabled={mfaLoading}
              className="bg-teal hover:bg-teal-deep rounded-full"
              data-testid="mfa-enable-btn"
            >
              {mfaLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : <KeyRound size={16} className="mr-2" />}
              Enable MFA
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
